import type { ReactNode } from "react";
import type { ModuleId, WeaponId } from "@frontier/content/definitions";
import type { PassiveId } from "@frontier/content/contracts";
import type { EquipmentKey } from "@frontier/content/arsenal";
import { AbilityIcon } from "./AbilityIcon";
const weaponArt: Record<WeaponId, ReactNode> = {
  pulse: (
    <>
      <path d="M11 26h18v9H11zM15 26V9h10v17M18 9V4h4v5M9 30H5m30 0h-4" />
      <path d="M18 14h4m-4 5h4" opacity=".5" />
    </>
  ),
  laser: (
    <>
      <path d="M12 27h16v8H12zM16 27V14h8v13M20 14V2M11 9l3 3m15-3-3 3" />
      <circle cx="20" cy="19" r="3" />
    </>
  ),
  missile: (
    <>
      <path d="m20 3 6 10v16H14V13zM14 23l-5 9 5-3m12-6 5 9-5-3M17 33v5m6-5v5" />
      <circle cx="20" cy="17" r="3" />
    </>
  ),
  flak: (
    <>
      <path d="M9 27h22v8H9zM10 27V8h5v19m3 0V4h5v23m3 0V8h5v19M6 31h3m22 0h3" />
      <path d="M10 13h5m3-4h5m3 4h5" opacity=".5" />
    </>
  ),
  rail: (
    <>
      <path d="M12 35V6h5v22h6V6h5v29zM17 11h6m-6 8h6M20 1v5M8 30h4m16 0h4" />
      <path d="M17 31h6" opacity=".5" />
    </>
  ),
  ion: (
    <>
      <path d="M12 28h16v7H12zM16 28V9h8v19M20 9V2" />
      <path d="M11 12c18 0 18 6 0 6s-1 6 18 6M8 7l3 2m21-2-3 2" />
    </>
  ),
};
const passiveArt: Record<PassiveId, ReactNode> = {
  none: (
    <>
      <path d="M8 4h24v32H8z" strokeDasharray="3 3" />
      <path d="M20 13v14m-7-7h14" />
    </>
  ),
  targeting: (
    <>
      <circle cx="20" cy="20" r="12" />
      <circle cx="20" cy="20" r="5" />
      <path d="M20 2v8m0 20v8M2 20h8m20 0h8" />
    </>
  ),
  capacitor: (
    <>
      <path d="M10 8h20v25H10zM15 4h10v4M14 35h12" />
      <path d="m22 12-7 10h7l-4 8" />
    </>
  ),
  "repair-drone": (
    <>
      <path d="m20 10 8 5v10l-8 5-8-5V15zM5 8l7 7m16 0 7-7M5 32l7-7m16 0 7 7" />
      <path d="M20 15v10m-5-5h10M2 5h6v6m24-6h6v6M2 29v6h6m24 0h6v-6" />
    </>
  ),
  thrusters: (
    <>
      <path d="M11 6h7v20h-7zM23 6h7v20h-7zM14 26v10m13-10v10M8 12h3m19 0h3" />
      <path d="m11 32 3 5 4-5m5 0 4 5 3-5" />
    </>
  ),
};
function ArtFrame({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}
function moduleBackdrop(id: ModuleId): string | null {
  return id === "barrier"
    ? "/assets/hangar/shield.png"
    : id === "repair" || id === "repair-field"
      ? "/assets/hangar/repair.png"
      : id === "jammer"
        ? "/assets/hangar/jammer.png"
        : null;
}

export function equipmentBackdrop(key: EquipmentKey | null): string | null {
  if (key?.kind === "system") return `/assets/hangar/${key.id}.png`;
  return key === null
    ? null
    : key.kind === "weapon"
      ? "/assets/hangar/weapon.png"
      : key.kind === "module"
        ? moduleBackdrop(key.id)
        : key.id === "repair-drone"
          ? "/assets/hangar/repair.png"
          : null;
}
export function EquipmentArt({
  equipment,
}: {
  equipment: EquipmentKey | null;
}) {
  if (equipment?.kind === "system") return <ArtFrame><image href={`/assets/hangar/${equipment.id}.png`} x="2" y="2" width="36" height="36" /></ArtFrame>;
  if (equipment?.kind === "module") return <AbilityIcon kind={equipment.id} />;
  return (
    <ArtFrame>
      {equipment?.kind === "weapon"
        ? weaponArt[equipment.id]
        : passiveArt[equipment?.id ?? "none"]}
    </ArtFrame>
  );
}
